// Single source of truth for Xpertpests policy terms, service prep instructions,
// and collection-letter copy. Edit here and it updates on every document.
import { escapeHtml } from './escapeHtml';

// ---- Standard Terms & Policies (shown on proposals, invoices, agreements) ----
export const TERMS = [
  {
    title: 'Service & Setup Details',
    body: 'Standard onboarding/setup fee: For any customer currently using, or looking to use, a pest control company, our standard onboarding fee is three times (3×) the monthly cost of your service. How to waive it: We want to save our customers money — you can completely waive this fee by enrolling a credit or debit card for automatic billing and keeping it current.',
  },
  {
    title: 'Billing & Payment Policy',
    body: 'You have thirty (30) days from the invoice date to send your check or clear your balance.',
  },
  {
    title: 'Late Payments & Returned Checks',
    body: 'If a check is returned/bounced, or if payment is not received within the 30-day window, a $20 late payment fee will be automatically applied to your account.',
  },
  {
    title: 'Service Warranty',
    body: 'We stand behind our work. If you notice a recurrence of the pest treated within two (2) weeks of your service, please contact us immediately for a free re-treatment under our service warranty. Your account must be fully paid and up to date to qualify — this warranty is completely void if the account is not paid in full.',
  },
  {
    title: 'Safety & Product Information',
    body: 'Product list: A complete list of products applied during your service is available upon request, or can be found on your attached service report. Pet & child safety: Please keep children and pets away from treated areas for the recommended safety period. Standard re-entry time is two (2) hours, or until all treated surfaces are completely dry.',
  },
  {
    title: 'Cancellation & Agreement Terms',
    body: 'Equipment retrieval: If this service agreement is terminated, any equipment we have on-site (such as bait/base stations) must be collected by our technician. Please inform us of your cancellation ahead of time so we can schedule this removal and avoid additional equipment fees. Early termination fee: Term agreements ensure you get the best long-term rates. If you choose to cancel your service before your agreement is fulfilled, an early termination fee equal to seventy percent (70%) of the remaining agreement balance will be applied to your final bill.',
  },
  {
    title: 'We’re Here to Help',
    body: 'Thank you for trusting us with your home and business. If you have any questions about your bill, safety guidelines, or your service, we’re always here to help.',
  },
];

// Compact HTML block of the terms, for embedding in printed documents.
export const termsHTML = () => `
  <div style="margin-top:24px;padding-top:12px;border-top:2px solid #8dd110">
    <div class="brand" style="font-size:13px;font-weight:800;margin-bottom:6px">SERVICE TERMS & POLICIES</div>
    ${TERMS.map((t) => `<p style="font-size:10.5px;line-height:1.5;color:#334155;margin:0 0 6px"><b>${t.title}.</b> ${t.body}</p>`).join('')}
  </div>`;

// Short, high-visibility payment-terms footer for the bottom of invoices, work
// orders, and proposals — the must-know points a customer should not miss.
export const paymentTermsFooterHTML = () => `
  <div style="margin-top:16px;padding:10px 12px;border:1px solid #8dd110;border-radius:8px;background:#f6faef">
    <div class="brand" style="font-size:12px;font-weight:800;margin-bottom:4px">PAYMENT TERMS</div>
    <ul style="margin:0;padding-left:16px;font-size:10.5px;line-height:1.6;color:#334155">
      <li>You have <b>30 days</b> from the invoice date to mail your payment or clear your balance.</li>
      <li>A <b>$20 late payment fee</b> is automatically applied to any balance not paid within 30 days, or to any returned/bounced check.</li>
      <li>To qualify for a warranty re-treatment / callback, your <b>account must be current and paid in full</b> — the warranty is void while an account is past due.</li>
      <li>Re-entry: keep children and pets off treated areas until dry (about <b>2 hours</b>).</li>
    </ul>
  </div>`;

// Accepted payment methods, shown on every document.
export const PAYMENT_METHODS = 'Visa · Mastercard · Discover · American Express · ACH · Zelle · Cash · Check';

// Closing footer for the bottom of every document: thank-you, referral offer,
// mailing/billing address, accepted payment methods, and links to company policies.
export const docFooterHTML = (contact = {}) => {
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
  const mailTo = contact.address || 'P.O. Box 4462, Carson, CA 90745';
  const phone = contact.phone1 || '562-215-8002';
  return `
  <div style="margin-top:16px;padding-top:12px;border-top:2px solid #8dd110;text-align:center">
    <p style="font-size:12px;color:#0f172a;font-weight:700;margin:0 0 2px">Thank you for your business — it's a pleasure to serve you!</p>
    <p style="font-size:11.5px;color:#5b8523;font-weight:700;margin:0 0 8px">★ Refer a friend and get a MONTH FREE — call for details! ${phone}</p>
    <p style="font-size:10.5px;color:#334155;margin:0 0 3px"><b>Mail payments to:</b> ${mailTo}</p>
    <p style="font-size:10.5px;color:#334155;margin:0 0 6px"><b>We accept:</b> ${PAYMENT_METHODS}</p>
    <p style="font-size:9.5px;color:#64748b;margin:0">
      <a href="${origin}/terms" style="color:#64748b;text-decoration:underline">Payment &amp; Service Policies</a> &nbsp;·&nbsp;
      <a href="${origin}/sms-policy" style="color:#64748b;text-decoration:underline">Communication Policy</a> &nbsp;·&nbsp;
      <a href="${origin}/privacy" style="color:#64748b;text-decoration:underline">Data &amp; Privacy Policy</a>
    </p>
  </div>`;
};

// Pesticide / product-list acknowledgment line + link. `listUrl` is where the
// customer can view the proposed products to be applied (defaults to the app's
// pesticide report tool).
export const pesticideAckHTML = (contact = {}, listUrl = '') => {
  const url = listUrl || `${(typeof window !== 'undefined' && window.location ? window.location.origin : '')}/pesticide-report.html`;
  return `
  <div style="margin-top:16px;padding:10px 12px;border:1px dashed #94a3b8;border-radius:8px">
    <div style="font-size:11px;font-weight:700;color:#0f172a;margin-bottom:4px">PESTICIDE / PRODUCT LIST ACKNOWLEDGMENT</div>
    <p style="font-size:10.5px;line-height:1.5;color:#334155;margin:0 0 6px">
      By signing below or accepting this document, you acknowledge that you have received the <b>proposed list of
      pesticides / products</b> to be applied during your service. The complete product list (with EPA registration
      numbers) is available upon request and can be viewed online here:
      <a href="${url}" style="color:#5b8523;font-weight:700">${url}</a>
    </p>
    <div style="display:flex;gap:24px;margin-top:10px;font-size:10.5px;color:#334155">
      <div style="flex:1;border-top:1px solid #94a3b8;padding-top:4px">Customer signature</div>
      <div style="width:140px;border-top:1px solid #94a3b8;padding-top:4px">Date</div>
    </div>
  </div>`;
};

// ---- Pre-service and post-service instructions, per pest ----
// Each entry: { pre: [...], post: [...], business: 'extra note for commercial accounts' }
export const SERVICE_INSTRUCTIONS = {
  'Cockroaches (Roaches)': {
    pre: [
      'Empty and wipe down kitchen cabinets, drawers, and pantry shelves where possible.',
      'Clear countertops; store food and pet food in sealed containers.',
      'Remove trash and clean up grease, crumbs, and spills.',
      'Fix leaks and dry any standing water — roaches need moisture.',
      'Move the refrigerator and stove slightly from the wall for access; keep kitchen and bathrooms accessible.',
    ],
    post: [
      'Stay out of treated areas until surfaces are dry (about 2 hours).',
      'Do NOT wipe or wash treated baseboards, cracks, and cabinet edges for at least 2 weeks — the bait/residual must stay.',
      'Expect increased roach activity for 1–2 weeks as they emerge and die — this is normal.',
      'Keep the area clean and dry and take out trash regularly.',
      'Do not apply store-bought sprays over our treatment — they repel roaches from the bait.',
    ],
    business: 'Commercial kitchens: schedule during off-hours, protect food-contact surfaces, and follow health-code cleanup after re-entry.',
  },
  'Rodents & Rats': {
    pre: [
      'Clear clutter along walls and in garages, attics, and storage areas so we can inspect and place stations.',
      'Store food, grains, and pet food in sealed metal or heavy plastic containers.',
      'Point out any entry points, droppings, or noises to the technician.',
      'Keep pets away from bait-station locations.',
    ],
    post: [
      'Do not move or open tamper-resistant bait stations; keep children and pets away from them.',
      'Expect a noticeable reduction over 1–2 weeks as rodents consume bait.',
      'Seal exterior gaps and holes larger than ¼ inch — ask us about exclusion work.',
      'Report any dead-rodent odor so we can locate and remove it.',
      'Continue sanitation: remove food and water sources.',
    ],
    business: 'Commercial accounts: we maintain a bait-station map and service log for your health inspections.',
  },
  'Ants': {
    pre: [
      'Wipe down counters and remove crumbs, spills, and sugary residue.',
      'Store food and pet food sealed; remove pet bowls during service if possible.',
      'Identify ant trails and entry points for the technician.',
      'Do not spray or clean the trails right before service.',
    ],
    post: [
      'Do NOT clean or spray over treated trails/entry points for 1–2 weeks — ants must carry the bait back to the colony.',
      'Expect increased ant activity for several days (they are recruiting to the bait) before the colony collapses.',
      'Keep surfaces dry and fix moisture sources.',
      'Avoid store-bought ant sprays — they repel ants from our bait.',
    ],
    business: 'Commercial accounts: schedule around food prep and we will document treated zones.',
  },
  'Spiders': {
    pre: [
      'Remove clutter, boxes, and debris from corners, garages, basements, and eaves.',
      'Knock down reachable webs, or we will during service.',
      'Trim vegetation touching the structure and reduce exterior lighting that attracts insects (spider food).',
    ],
    post: [
      'Stay out of treated areas until dry (about 2 hours).',
      'Leave treated corners and eaves undisturbed; do not wash them for 2 weeks.',
      'Keep removing new webs — with fewer prey insects, spider populations drop over 2–4 weeks.',
    ],
    business: 'Commercial buildings: we focus on entryways, docks, and exterior lighting where spiders concentrate.',
  },
};

// Printable HTML for a pest's prep sheet (home or business).
export const instructionsHTML = (pest, kind = 'home', contact = {}) => {
  const d = SERVICE_INSTRUCTIONS[pest];
  if (!d) return '';
  const li = (arr) => arr.map((x) => `<li style="margin:0 0 5px;font-size:12px;line-height:1.5">${x}</li>`).join('');
  return `
    <div style="border-bottom:3px solid #8dd110;padding-bottom:12px;margin-bottom:16px">
      <div class="brand" style="font-size:22px">${(contact.companyName || 'Xpertpests Solutions').toUpperCase()}</div>
      <div style="font-size:12px;color:#475569">${contact.phone1 || ''} · ${contact.email || ''}</div>
    </div>
    <h2 style="margin:0 0 2px">${pest} — Service Instructions</h2>
    <div style="font-size:12px;color:#475569;margin-bottom:14px;text-transform:capitalize">${kind}</div>
    <h3 style="margin:0 0 6px">Before Your Service</h3>
    <ul style="margin:0 0 16px;padding-left:18px">${li(d.pre)}</ul>
    <h3 style="margin:0 0 6px">After Your Service</h3>
    <ul style="margin:0 0 16px;padding-left:18px">${li(d.post)}</ul>
    ${kind === 'business' && d.business ? `<p style="font-size:12px;color:#334155"><b>For businesses:</b> ${d.business}</p>` : ''}`;
};

// ---- Collection letter copy ----
export const collectionLetterHTML = (inv, name, daysPastDue, balance, contact = {}) => {
  // Every value here can come from a customer record, an import or settings,
  // so all of it is escaped before it becomes markup in the print window.
  const e = escapeHtml;
  const company = contact.companyName || 'Xpertpests Solutions';
  return `
  <div style="border-bottom:3px solid #8dd110;padding-bottom:12px;margin-bottom:20px">
    <div class="brand" style="font-size:22px">${e(company.toUpperCase())}</div>
    <div style="font-size:12px;color:#475569">${e(contact.address || '')} · ${e(contact.phone1 || '')} · ${e(contact.email || '')}</div>
  </div>
  <div style="font-size:12px;color:#475569;margin-bottom:16px">Date: ${e(new Date().toLocaleDateString())}</div>
  <p style="font-size:13px">Dear ${e(name)},</p>
  <p style="font-size:13px;line-height:1.6">Our records show that invoice <b>${e(inv.number || inv.id)}</b> in the amount of <b>${e(balance)}</b> is currently <b>${e(daysPastDue)} day(s) past due</b>. Per our billing policy, payment is due within 30 days of the invoice date, and a late fee is applied to balances not paid within that window.</p>
  <p style="font-size:13px;line-height:1.6">Please remit payment at your earliest convenience to keep your account in good standing and to remain eligible for our service warranty (the warranty is void while an account is past due). You can pay by check, or by card using the payment link on your invoice.</p>
  <p style="font-size:13px;line-height:1.6">If you have already sent payment, please disregard this notice — and thank you. If you have any questions about your bill, we are always here to help.</p>
  <p style="font-size:13px;margin-top:20px">Sincerely,<br/>${e(company)}<br/>${e(contact.phone1 || '')} · ${e(contact.email || '')}</p>`;
};
